import type { MediaKind } from "../domain/types";

/**
 * Canonical object-key layout for UN8 media (local IndexedDB and cloud).
 * Never invent alternate layouts in adapters — always call buildStorageKey.
 *
 *   un8/{userId}/{kind}/{assetId}/{safeFileName}
 */
export const STORAGE_PREFIX = "un8";

const KINDS: readonly MediaKind[] = [
  "government_id",
  "confirmation_video",
  "profile_photo",
];

export function isMediaKind(v: string): v is MediaKind {
  return (KINDS as readonly string[]).includes(v);
}

export function sanitizeUserId(userId: string): string {
  const s = (userId || "anonymous").trim().replace(/[^a-zA-Z0-9._-]+/g, "_");
  return s.slice(0, 128) || "anonymous";
}

export function sanitizeFileName(name: string): string {
  const base = (name || "file").split(/[/\\]/).pop() || "file";
  const cleaned = base.replace(/[^a-zA-Z0-9._-]+/g, "_").slice(0, 120);
  return cleaned || "file";
}

export function sanitizeAssetId(assetId: string): string {
  const s = (assetId || "asset").replace(/[^a-zA-Z0-9._-]+/g, "_");
  return s.slice(0, 80) || "asset";
}

/** Extension fallback when filename lacks one. */
export function extensionForMime(mime: string): string {
  const m = (mime || "").toLowerCase();
  if (m === "image/jpeg" || m === "image/jpg") return ".jpg";
  if (m === "image/png") return ".png";
  if (m === "image/webp") return ".webp";
  if (m === "image/gif") return ".gif";
  if (m === "application/pdf") return ".pdf";
  if (m === "video/webm") return ".webm";
  if (m === "video/mp4") return ".mp4";
  if (m === "video/quicktime") return ".mov";
  return "";
}

export function ensureFileName(fileName: string, mimeType: string): string {
  const safe = sanitizeFileName(fileName);
  if (/\.[a-zA-Z0-9]{1,8}$/.test(safe)) return safe;
  return `${safe}${extensionForMime(mimeType)}` || `file${extensionForMime(mimeType) || ".bin"}`;
}

/**
 * Build the one true storage key. Both local and cloud adapters MUST use this.
 * Re-running with the same inputs always yields the same key (stable).
 */
export function buildStorageKey(input: {
  userId: string;
  kind: MediaKind;
  assetId: string;
  fileName: string;
  mimeType?: string;
}): string {
  if (!isMediaKind(input.kind)) {
    throw new Error(`Invalid media kind: ${input.kind}`);
  }
  const userId = sanitizeUserId(input.userId);
  const assetId = sanitizeAssetId(input.assetId);
  const file = ensureFileName(input.fileName, input.mimeType || "");
  return `${STORAGE_PREFIX}/${userId}/${input.kind}/${assetId}/${file}`;
}

/** True if key matches the canonical layout. */
export function isCanonicalStorageKey(key: string): boolean {
  return parseStorageKey(key) !== null;
}

/** Parse a key built by buildStorageKey. */
export function parseStorageKey(key: string): {
  userId: string;
  kind: MediaKind;
  assetId: string;
  fileName: string;
} | null {
  if (!key || typeof key !== "string") return null;
  const parts = key.split("/").filter(Boolean);
  if (parts.length < 5 || parts[0] !== STORAGE_PREFIX) return null;
  if (!isMediaKind(parts[2])) return null;
  return {
    userId: parts[1],
    kind: parts[2],
    assetId: parts[3],
    fileName: parts.slice(4).join("/"),
  };
}

/**
 * Assert adapter returned the expected key (prevents silent key drift).
 * Throws if mismatch — uploadService marks asset failed.
 */
export function assertSameStorageKey(expected: string, actual: string): void {
  if (expected !== actual) {
    throw new Error(
      `storageKey mismatch: expected "${expected}" but adapter returned "${actual}"`
    );
  }
  if (!isCanonicalStorageKey(actual)) {
    throw new Error(`Non-canonical storageKey: "${actual}"`);
  }
}

/** Build HTTPS public URL from CDN base + storageKey (no double slashes). */
export function joinPublicUrl(publicBaseUrl: string, storageKey: string): string {
  const base = publicBaseUrl.replace(/\/+$/, "");
  const key = storageKey.replace(/^\/+/, "");
  return `${base}/${key}`;
}

export function isHttpsUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  try {
    const u = new URL(url);
    return u.protocol === "https:";
  } catch {
    return false;
  }
}
